import { useCallback, useEffect, useMemo, useState } from "react"

import styles from "./index.module.scss"
import ConfigIcon from "../../../assets/icons/ConfigIcon"
import RcmpLoading from "../../atoms/Loading"
import RcmpHeader from "../../atoms/Header"
import ContentBlank from "../../molecules/ContentBlank"
import { Switch } from "@stardust-ds/react"
import Button from "../../atoms/Button"
import Disclosure, { DisclosureHeader, DisclosureContent } from "../../atoms/Disclosure"
import QualityPanelIcon from "../../../assets/icons/MedalIcon"
import AuditMgmtIcon from "../../../assets/icons/CheckedIcon"
import OpPanelIcon from "../../../assets/icons/DashboardIcon"
import DocumentIcon from "../../../assets/icons/DocumentIcon"
import CIQIcon from "../../../assets/icons/CIQIcon"
import UsersIcon from "../../../assets/icons/TeamIcon"
import VoucherForm from "./components/VoucherForm"
import NumberInput from "./components/NumberInput"
import intl from "react-intl-universal"
import ModalFinancialForm from "./components/ModalFinancialForm"
import { usePlanContext } from "./context"
import PlanCard from "./components/PlanCard"
import { useAppearanceContext } from "../../../contexts/Appearance"
import ListDiff from "./components/ListDiff"

export default function PlanPage() {
  const {
    aModules,
    bIsLoading,
    nDiffUsers,
    nDiffSpace,
    aDiffModules,
    bIsAnnual,
    aHiredModules,
    oCustomer,
  } = usePlanContext()
  const [bIsOpenModalFinancial, setIsOpenModalFinancial] = useState(false)
  const { oConfig: oConfigWL } = useAppearanceContext()

  const [bIsAnnualPlan, setIsAnnualPlan] = useState(bIsAnnual)

  const bDisableHire = useMemo(
    () =>
      bIsLoading ||
      aHiredModules.length === 0 ||
      (nDiffUsers === 0 && nDiffSpace === 0 && aDiffModules.length === 0),
    [bIsLoading, nDiffUsers, nDiffSpace, aDiffModules, aHiredModules],
  )

  const handleOpenModalFinancial = useCallback((bAnnual) => {
    setIsOpenModalFinancial(true)
    setIsAnnualPlan(bAnnual)
  }, [])

  const handleCloseModalFinancial = useCallback(() => {
    setIsOpenModalFinancial(false)
    setIsAnnualPlan(false)
  }, [])

  const bIsWL = useMemo(() => oConfigWL === null || !!oConfigWL.nWlpnId, [oConfigWL])

  return (
    <>
      <RcmpHeader
        cTitlePage={"Planos"}
        elIcon={<ConfigIcon />}
        aBreadCrumbs={[]}
        className={styles.header}
      />
      <ContentBlank
        bSwitchable={false}
        bDisableActions
        bCanShowActions={false}
        bShowFilter={false}
        bCanSearch={false}
        className={styles.contentBlank}
      >
        {bIsLoading ? (
          <div className={styles.loadingContainer}>
            <RcmpLoading bShow={true} size={60} />
          </div>
        ) : (
          <div className={styles.container}>
            <div className={styles.productsOverview}>
              <div className={styles.pageIntro}>
                {bIsWL ? (
                  <>
                    <h3>{intl.get("plan.intro.title_wl")}</h3>
                    <p>
                      {intl.get(
                        oCustomer.bIsTrial
                          ? "plan.intro.text_hire_wl"
                          : "plan.intro.text_update_wl",
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <h3>{intl.get("plan.intro.title")}</h3>
                    <p>{intl.get("plan.intro.text_p1")}</p>
                    <p>{intl.get("plan.intro.text_p2")}</p>
                  </>
                )}
              </div>
              <div>
                <UserDisclosure bIsWL={bIsWL} />
                <SpaceDisclosure bIsWL={bIsWL} />
                {aModules.map((oModule) => (
                  <ModuleDisclosure
                    oModule={oModule}
                    key={oModule.mod_nid}
                    bIsWL={bIsWL}
                  />
                ))}
              </div>
            </div>
            {!bIsWL && (
              <div className={styles.orderSummary}>
                <div className={styles.voucherForm}>
                  <VoucherForm />
                </div>
                <div className={styles.planTypes}>
                  <PlanCard />
                </div>
                <ListDiff />
                <div className={styles.hiringContainer}>
                  <Button
                    variant="primary"
                    onClick={() => handleOpenModalFinancial(false)}
                    disabled={bDisableHire}
                  >
                    {intl.get("plan.hire_monthly")}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleOpenModalFinancial(true)}
                    disabled={bDisableHire}
                  >
                    {intl.get("plan.hire_annually")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </ContentBlank>
      {bIsOpenModalFinancial && (
        <ModalFinancialForm
          onClose={() => handleCloseModalFinancial(false)}
          bIsAnnual={bIsAnnualPlan}
        />
      )}
    </>
  )
}

function UserDisclosure({ bIsWL = true }) {
  const { nHiredUsers, setHiredUsers, oSystemConfig, oCustomer } = usePlanContext()

  const nMinUsers = useMemo(
    () => Math.max(oSystemConfig.default_total_users, oCustomer.nUserCount),
    [oCustomer, oSystemConfig],
  )
  return (
    <Disclosure className={styles.disclosure}>
      <DisclosureHeader cArrowPosition="right" titleClassName={styles.disclosureTitle}>
        <div className={styles.productIcon}>
          <UsersIcon fill="#000" />
        </div>
        <span>{intl.get("plan.users")}</span>
      </DisclosureHeader>
      <DisclosureContent className={styles.disclosureContent}>
        <NumberInput
          value={nHiredUsers === 0 ? "" : nHiredUsers}
          onChange={(evtChange) => setHiredUsers(+evtChange.target.value || 0)}
          disabled={bIsWL}
          className={nHiredUsers < nMinUsers ? styles.inputInvalid : ""}
          onBlur={(evtBlur) => setHiredUsers(Math.max(+evtBlur.target.value, nMinUsers))}
        />
        <div className={styles.inputDescription}>
          {intl.get("plan.minimum")}: {nMinUsers}
        </div>
      </DisclosureContent>
    </Disclosure>
  )
}

function SpaceDisclosure({ bIsWL = true }) {
  const { nHiredSpace, setHiredSpace, oSystemConfig, oCustomer } = usePlanContext()

  const nMinSpace = useMemo(() => {
    const nUsedSpaceGb = Math.ceil(oCustomer.cust_nused_space_quota_mb / 1024)
    return Math.max(oSystemConfig.min_space, nUsedSpaceGb)
  }, [oCustomer, oSystemConfig])

  return (
    <Disclosure className={styles.disclosure}>
      <DisclosureHeader cArrowPosition="right" titleClassName={styles.disclosureTitle}>
        <div className={styles.productIcon}></div>
        <span>{intl.get("plan.storage")}</span>
      </DisclosureHeader>
      <DisclosureContent className={styles.disclosureContent}>
        <div className={styles.inputUnitContainer}>
          <NumberInput
            value={nHiredSpace === 0 ? "" : nHiredSpace}
            onChange={(evtChange) => setHiredSpace(+evtChange.target.value || 0)}
            disabled={bIsWL}
            className={nHiredSpace < nMinSpace ? styles.inputInvalid : ""}
            onBlur={(evtBlur) =>
              setHiredSpace(Math.max(+evtBlur.target.value, nMinSpace))
            }
          />
          <span className={styles.inputDescription} style={{ marginTop: 0 }}>
            GB
          </span>
        </div>
        <div className={styles.inputDescription}>
          {intl.get("plan.minimum")}: {nMinSpace} GB
        </div>
      </DisclosureContent>
    </Disclosure>
  )
}

const A_MODULE_ICONS = [
  <QualityPanelIcon key="qp" fill="#000" />,
  <OpPanelIcon key="op" fill="#000" />,
  <AuditMgmtIcon key="am" fill="#000" />,
  <DocumentIcon key="doc" fill="#000" />,
  <CIQIcon key="ciq" fill="#000" />,
]
function ModuleDisclosure({ oModule, bIsWL = true }) {
  const { aHiredModules, oHiredModulesOld, addModule, removeModule, oCustomer } =
    usePlanContext()

  const bIsHired = useMemo(() => {
    return aHiredModules.includes(oModule.mod_nid)
  }, [aHiredModules, oModule])

  useEffect(() => {
    if (oCustomer.bIsTrial && Object.keys(oHiredModulesOld).length === 0) {
      addModule(oModule.mod_nid)
    }
  }, [oHiredModulesOld])

  return (
    <Disclosure className={styles.disclosure}>
      <DisclosureHeader cArrowPosition="right" titleClassName={styles.moduleName}>
        <div className={styles.productIcon}>{A_MODULE_ICONS[oModule.mod_nid - 1]}</div>
        <span>{oModule.mod_cname}</span>
        {!bIsWL && (
          <span
            className={styles.switchWrapper}
            onClick={(evtClick) => evtClick.stopPropagation()}
          >
            <Switch
              checked={bIsHired}
              onChange={() =>
                bIsHired ? removeModule(oModule.mod_nid) : addModule(oModule.mod_nid)
              }
              className={bIsHired ? styles.switchOn : styles.switchOff}
            />
          </span>
        )}
      </DisclosureHeader>
      <DisclosureContent className={styles.disclosureContent}>
        {oModule.mod_cdescription}
      </DisclosureContent>
    </Disclosure>
  )
}
